import { useState } from 'react';
import type React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronRight, Copy, Download } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import type { Artifact } from '@/types';

// Utility function to process bold text formatting (same as TextRenderer)
const processBoldText = (text: string, keyPrefix: string): React.ReactNode => {
  const parts = text.split(/(\*\*.*?\*\*)/g);
  
  return (
    <>
      {parts.map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          const boldText = part.slice(2, -2);
          return (
            <span key={`${keyPrefix}-bold-${index}`} className="font-bold">
              {boldText}
            </span>
          );
        } else {
          return part || null;
        }
      })}
    </>
  );
};

interface JsonRendererProps {
  artifact: Artifact;
}

interface JsonNodeProps {
  data: any;
  keyName?: string;
  level?: number;
  isLast?: boolean;
}

function JsonNode({ data, keyName, level = 0, isLast = true }: JsonNodeProps) {
  const [isOpen, setIsOpen] = useState(level < 2); // Auto-expand first 2 levels
  
  // const indent = '  '.repeat(level); // Unused for now
  const isObject = data !== null && typeof data === 'object' && !Array.isArray(data);
  const isArray = Array.isArray(data);
  const isExpandable = isObject || isArray;
  
  const getValueType = (value: any): string => {
    if (value === null) return 'null';
    if (Array.isArray(value)) return 'array';
    return typeof value;
  };

  const renderValue = (value: any) => {
    const type = getValueType(value);
    
    switch (type) {
      case 'string':
        return <span className="text-green-600 dark:text-green-400">"{value}"</span>;
      case 'number':
        return <span className="text-blue-600 dark:text-blue-400">{value}</span>;
      case 'boolean':
        return <span className="text-purple-600 dark:text-purple-400">{String(value)}</span>;
      case 'null':
        return <span className="text-gray-500">null</span>;
      default:
        return <span>{String(value)}</span>;
    }
  };

  const getCollectionInfo = () => {
    if (isArray) {
      return `Array(${data.length})`;
    } else if (isObject) {
      const keys = Object.keys(data);
      return `Object(${keys.length})`;
    }
    return '';
  };

  if (!isExpandable) {
    return (
      <div className="font-mono text-sm">
        {keyName && (
          <>
            <span className="text-blue-700 dark:text-blue-300">"{keyName}"</span>
            <span className="text-gray-600 dark:text-gray-400">: </span>
          </>
        )}
        {renderValue(data)}
        {!isLast && <span className="text-gray-600 dark:text-gray-400">,</span>}
      </div>
    );
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="font-mono text-sm">
        <CollapsibleTrigger asChild>
          <button className="flex items-center gap-1 hover:bg-muted/50 rounded px-1 -ml-1">
            {isOpen ? (
              <ChevronDown className="h-3 w-3 text-gray-500" />
            ) : (
              <ChevronRight className="h-3 w-3 text-gray-500" />
            )}
            {keyName && (
              <>
                <span className="text-blue-700 dark:text-blue-300">"{keyName}"</span>
                <span className="text-gray-600 dark:text-gray-400">: </span>
              </>
            )}
            <span className="text-gray-600 dark:text-gray-400">
              {isArray ? '[' : '{'}
            </span>
            {!isOpen && (
              <>
                <span className="text-xs text-muted-foreground ml-1">
                  {getCollectionInfo()}
                </span>
                <span className="text-gray-600 dark:text-gray-400 ml-1">
                  {isArray ? ']' : '}'}
                </span>
              </>
            )}
          </button>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <div className="ml-4 border-l border-gray-200 dark:border-gray-700 pl-4 mt-1">
            {isArray ? (
              data.map((item: any, index: number) => (
                <JsonNode
                  key={index}
                  data={item}
                  keyName={String(index)}
                  level={level + 1}
                  isLast={index === data.length - 1}
                />
              ))
            ) : (
              Object.entries(data).map(([key, value], index, entries) => (
                <JsonNode
                  key={key}
                  data={value}
                  keyName={key}
                  level={level + 1}
                  isLast={index === entries.length - 1}
                />
              ))
            )}
          </div>
          <div className="font-mono text-sm text-gray-600 dark:text-gray-400">
            {isArray ? ']' : '}'}
            {!isLast && ','}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

// Function to format text with markdown-like syntax (same as TextRenderer)
function formatText(text: string): React.ReactNode {
  return (
    <div className="space-y-4">
      {text.split('\n\n').map((paragraph, pIndex) => {
        const lines = paragraph.split('\n');
        return (
          <div key={`paragraph-${pIndex}`} className="space-y-2">
            {renderParagraphContent(lines, pIndex)}
          </div>
        );
      })}
    </div>
  );
}

function renderParagraphContent(lines: string[], paragraphIndex: number): React.ReactNode[] {
  const elements: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmedLine = line.trim();
    const bulletMatch = line.match(/^(\s*)[*•-]\s+(.*)$/);

    if (bulletMatch) {
      const listItems: React.ReactNode[] = [];
      
      while (i < lines.length) {
        const currentLine = lines[i];
        const currentBulletMatch = currentLine.match(/^(\s*)[*•-]\s+(.*)$/);

        if (currentBulletMatch) {
          const [, indent, content] = currentBulletMatch;
          const spaceCount = indent.length;
          const indentLevel = Math.floor(spaceCount / 4);

          listItems.push(
            <li
              key={`li-${paragraphIndex}-${i}`}
              className="text-sm leading-relaxed mb-1"
              style={{
                marginLeft: indentLevel > 0 ? `${indentLevel * 1.5}rem` : '0',
                listStyleType: indentLevel === 0 ? 'disc' : indentLevel === 1 ? 'circle' : 'square'
              }}
            >
              {processBoldText(content, `li-${paragraphIndex}-${i}`)}
            </li>
          );
          i++;
        } else {
          break;
        }
      }

      if (listItems.length > 0) {
        elements.push(
          <ul key={`list-${paragraphIndex}-${elements.length}`} className="pl-6">
            {listItems}
          </ul>
        );
      }
    } else {
      if (trimmedLine) {
        const leadingSpaces = line.match(/^(\s*)/)?.[1] || '';
        const indentStyle = leadingSpaces ? {
          paddingLeft: `${leadingSpaces.length * 0.5}rem`
        } : undefined;

        elements.push(
          <div
            key={`line-${paragraphIndex}-${i}`}
            className="text-sm leading-relaxed"
            style={indentStyle}
          >
            {processBoldText(trimmedLine, `line-${paragraphIndex}-${i}`)}
          </div>
        );
      } else {
        elements.push(
          <div key={`empty-${paragraphIndex}-${i}`} className="h-2" />
        );
      }
      i++;
    }
  }

  return elements;
}

export function JsonRenderer({ artifact }: JsonRendererProps) {
  
  const jsonData = (() => {
    try {
      return typeof artifact.data === 'string' 
        ? JSON.parse(artifact.data) 
        : artifact.data;
    } catch (error) {
      console.error('Error parsing JSON:', error);
      return null;
    }
  })();

  // Check if JSON contains formatted text that should be rendered
  const extractedText = (() => {
    if (!jsonData) return null;
    
    // Check for common patterns where text content is nested in JSON
    if (jsonData.parts && Array.isArray(jsonData.parts) && jsonData.parts[0]?.text) {
      return jsonData.parts[0].text;
    }
    if (jsonData.text && typeof jsonData.text === 'string') {
      return jsonData.text;
    }
    if (jsonData.content && typeof jsonData.content === 'string') {
      return jsonData.content;
    }
    if (jsonData.message && typeof jsonData.message === 'string') {
      return jsonData.message;
    }
    
    return null;
  })();

  const hasFormattedText = extractedText && (
    extractedText.includes('**') || 
    /^(\s*)[*•-]\s+/m.test(extractedText)
  );

  const [viewMode, setViewMode] = useState<'tree' | 'raw' | 'text'>(
    hasFormattedText ? 'text' : 'tree'
  );

  const formattedJson = jsonData ? JSON.stringify(jsonData, null, 2) : '';

  const handleCopy = () => {
    navigator.clipboard.writeText(formattedJson);
  };

  const handleDownload = () => {
    const blob = new Blob([formattedJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${artifact.name.replace(/\.[^/.]+$/, '')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!jsonData) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Unable to parse JSON data. The data may be malformed.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {hasFormattedText && (
            <Button
              variant={viewMode === 'text' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('text')}
            >
              Formatted Text
            </Button>
          )}
          <Button
            variant={viewMode === 'tree' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('tree')}
          >
            Tree View
          </Button>
          <Button
            variant={viewMode === 'raw' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('raw')}
          >
            Raw JSON
          </Button>
          <Badge variant="secondary" className="text-xs">
            {Array.isArray(jsonData) ? `${jsonData.length} items` : `${Object.keys(jsonData).length} keys`}
          </Badge>
          {hasFormattedText && (
            <Badge variant="outline" className="text-xs">
              Contains formatted text
            </Badge>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
          >
            <Copy className="h-4 w-4" />
            Copy
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownload}
          >
            <Download className="h-4 w-4" />
            Download
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="border rounded-lg p-4 bg-muted/30 max-h-96 overflow-auto">
        {viewMode === 'text' && extractedText ? (
          <div className="prose prose-sm max-w-none">
            {formatText(extractedText)}
          </div>
        ) : viewMode === 'tree' ? (
          <JsonNode data={jsonData} />
        ) : (
          <pre className="text-sm whitespace-pre-wrap break-words font-mono">
            {formattedJson}
          </pre>
        )}
      </div>
    </div>
  );
}